import React, { useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Trash2, Truck } from 'lucide-react';
import { Dialog } from '../reusable';
import { getDeliveryStatusStyle, normalizeDeliveryStatus } from '../../utils/deliveryStatus';

const theme = (isDarkMode) =>
  isDarkMode
    ? {
        cardBg: '#27272a',
        cardBorder: '#3f3f46',
        tableHead: '#1f1f23',
        text: '#f4f4f5',
        muted: '#a1a1aa',
        sectionBg: '#1f1f23',
      }
    : {
        cardBg: '#ffffff',
        cardBorder: '#d8dee8',
        tableHead: '#f1f5f9',
        text: '#0f172a',
        muted: '#64748b',
        sectionBg: '#f8fafc',
      };

const formatDate = (value) => {
  if (!value) return '—';
  const raw = String(value).trim();
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(raw);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatDate(value);
  return date.toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatNumber = (value, digits = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

const formatMoney = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const Card = ({ t, children, style }) => (
  <div
    style={{
      marginBottom: 12,
      borderRadius: 10,
      border: `1px solid ${t.cardBorder}`,
      background: t.cardBg,
      padding: '14px 16px',
      ...style,
    }}
  >
    {children}
  </div>
);

const Field = ({ label, value, t, emphasize = false }) => (
  <div style={{ minWidth: 0, lineHeight: 1.2 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, marginBottom: 3 }}>{label}</div>
    <div
      style={{
        fontSize: emphasize ? 14 : 13,
        fontWeight: emphasize ? 700 : 600,
        color: t.text,
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {value || '—'}
    </div>
  </div>
);

const SectionTitle = ({ children, t }) => (
  <div style={{ margin: '4px 2px 8px', fontSize: 14, fontWeight: 700, color: t.text }}>{children}</div>
);

const StatusBadge = ({ status }) => {
  const normalized = normalizeDeliveryStatus(status || 'Programada');
  const palette = getDeliveryStatusStyle(normalized);
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: palette.bg,
        color: palette.text,
        border: `1px solid ${palette.border}`,
      }}
    >
      {normalized}
    </span>
  );
};

const thStyle = (t, align = 'left') => ({
  padding: '8px 10px',
  textAlign: align,
  fontSize: 11,
  fontWeight: 600,
  color: t.muted,
  borderBottom: `1px solid ${t.cardBorder}`,
  whiteSpace: 'nowrap',
});

const tdStyle = (t, align = 'left') => ({
  padding: '8px 10px',
  fontSize: 12,
  textAlign: align,
  borderBottom: `1px solid ${t.cardBorder}`,
  color: t.text,
  verticalAlign: 'top',
});

/**
 * Ficha de solo lectura de una entrega: datos generales, tramos/viajes e ítems.
 */
export const DeliveryDetailModal = ({
  isOpen,
  onClose,
  onEdit,
  onEditLeg,
  onDeleteLeg,
  onEditTrip,
  onSaveItems,
  availableItems = [],
  isDarkMode = false,
  delivery = null,
  legs = [],
  items = [],
  loading = false,
}) => {
  const t = theme(isDarkMode);
  const [editingItems, setEditingItems] = useState(false);
  const [draftItems, setDraftItems] = useState([]);
  const [itemsSaving, setItemsSaving] = useState(false);
  const [itemsError, setItemsError] = useState('');

  const sortedLegs = useMemo(
    () =>
      [...(legs || [])].sort(
        (a, b) =>
          Number(a.sequence || 0) - Number(b.sequence || 0) ||
          Number(a.stop_order || 0) - Number(b.stop_order || 0) ||
          Number(a.id || 0) - Number(b.id || 0)
      ),
    [legs]
  );

  const legsCostTotal = useMemo(
    () => sortedLegs.reduce((sum, leg) => sum + Number(leg.allocated_cost || 0), 0),
    [sortedLegs]
  );

  const itemRows = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  // Salir del modo edición al cambiar de entrega o cerrar la ficha.
  useEffect(() => {
    setEditingItems(false);
    setDraftItems([]);
    setItemsError('');
  }, [delivery?.id, isOpen]);

  const canEditItems = typeof onSaveItems === 'function';

  const startEditingItems = () => {
    setDraftItems(
      itemRows.map((item, index) => ({
        _key: `row-${item.id || item.quotation_item_id || item.product_sku || index}-${index}`,
        quotation_item_id: Number(item.quotation_item_id || 0),
        product_sku: item.product_sku || '',
        product_name: item.product_name || item.item_name || '',
        color: item.color || '',
        unit: item.unit || '',
        quantity: Number(item.quantity || item.quoted_qty || 0),
        unit_price: Number(item.unit_price || 0),
      }))
    );
    setItemsError('');
    setEditingItems(true);
  };

  const cancelEditingItems = () => {
    setEditingItems(false);
    setDraftItems([]);
    setItemsError('');
  };

  const updateDraftItem = (key, field, value) => {
    setDraftItems((prev) =>
      prev.map((row) => (row._key === key ? { ...row, [field]: Math.max(0, Number(value || 0)) } : row))
    );
  };

  const removeDraftItem = (key) => {
    setDraftItems((prev) => prev.filter((row) => row._key !== key));
  };

  // Ítems cotizados de la venta que aún no están en la entrega.
  const addableItems = useMemo(() => {
    const used = new Set(
      draftItems.map((row) => String(row.quotation_item_id || row.product_sku || ''))
    );
    return (availableItems || []).filter(
      (item) => !used.has(String(item.quotation_item_id || item.product_sku || ''))
    );
  }, [availableItems, draftItems]);

  const addDraftItem = (identifier) => {
    const source = (availableItems || []).find(
      (item) => String(item.quotation_item_id || item.product_sku || '') === String(identifier)
    );
    if (!source) return;
    setDraftItems((prev) => [
      ...prev,
      {
        _key: `new-${identifier}-${prev.length}`,
        quotation_item_id: Number(source.quotation_item_id || 0),
        product_sku: source.product_sku || '',
        product_name: source.product_name || source.item_name || '',
        color: source.color || '',
        unit: source.unit || '',
        quantity: Number(source.quantity || source.quoted_qty || 0),
        unit_price: Number(source.unit_price || 0),
      },
    ]);
  };

  const saveItems = async () => {
    setItemsSaving(true);
    setItemsError('');
    try {
      await onSaveItems(
        delivery.id,
        draftItems.map((row) => ({
          quotation_item_id: Number(row.quotation_item_id || 0),
          product_sku: row.product_sku || '',
          product_name: row.product_name || '',
          quantity: Number(row.quantity || 0),
          unit_price: Number(row.unit_price || 0),
          line_total: Math.round(Number(row.quantity || 0) * Number(row.unit_price || 0) * 100) / 100,
        }))
      );
      setEditingItems(false);
      setDraftItems([]);
    } catch (error) {
      setItemsError(error?.message || 'No se pudieron guardar los ítems.');
    } finally {
      setItemsSaving(false);
    }
  };

  if (!delivery) return null;

  const status = normalizeDeliveryStatus(delivery.delivery_status || 'Programada');
  const dispatchTipo =
    delivery.dispatch_tipo ||
    delivery.delivery_kind ||
    delivery.delivery_type ||
    '—';

  const ghostBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 30,
    padding: '0 12px',
    borderRadius: 8,
    border: `1px solid ${t.cardBorder}`,
    background: t.sectionBg,
    color: t.text,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  };

  const primaryBtn = {
    ...ghostBtn,
    border: 'none',
    background: '#2563eb',
    color: '#fff',
  };

  const iconBtn = {
    width: 30,
    height: 30,
    borderRadius: 8,
    border: `1px solid ${t.cardBorder}`,
    background: t.sectionBg,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  };

  const cellInput = {
    width: 100,
    height: 28,
    borderRadius: 6,
    border: `1px solid ${t.cardBorder}`,
    background: t.cardBg,
    color: t.text,
    fontSize: 12,
    textAlign: 'right',
    padding: '0 6px',
    outline: 'none',
  };

  const content = (
    <div>
      <Card t={t}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            marginBottom: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: t.text }}>
              {delivery.delivery_code || `Entrega #${delivery.id}`}
            </div>
            <div style={{ fontSize: 13, color: t.muted, marginTop: 4 }}>
              {[delivery.sale_code, delivery.quotation_code, delivery.organization_name]
                .filter(Boolean)
                .join(' · ') || 'Sin venta asociada'}
            </div>
          </div>
          <StatusBadge status={status} />
        </div>

        <SectionTitle t={t}>Información general</SectionTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '12px 16px',
          }}
        >
          <Field label="Tipo" value={dispatchTipo} t={t} />
          <Field label="Estado" value={status} t={t} />
          <Field label="Fecha programada envío" value={formatDate(delivery.scheduled_date)} t={t} />
          <Field
            label="Fecha programada recepción"
            value={formatDate(delivery.expected_delivery_date || delivery.promised_delivery_date)}
            t={t}
          />
          <Field label="Fecha límite" value={formatDate(delivery.promised_delivery_date)} t={t} />
          <Field label="Fecha entrega real" value={formatDate(delivery.delivered_date)} t={t} />
          <Field label="Inicio operativo" value={formatDateTime(delivery.started_at)} t={t} />
          <Field label="Peso (kg)" value={formatNumber(delivery.weight_kg)} t={t} />
          <Field label="Bultos" value={formatNumber(delivery.package_count, 0)} t={t} />
          <Field label="Guía de despacho" value={delivery.dispatch_guide} t={t} />
          <Field label="Factura" value={delivery.invoice_number} t={t} />
          <Field label="Creado por" value={delivery.created_by} t={t} />
          <Field label="Origen" value={delivery.pickup_warehouse} t={t} />
          <Field label="Dirección de entrega" value={delivery.delivery_address} t={t} />
          <Field label="Contacto recepción" value={delivery.receiver_name} t={t} />
          <Field label="Fono recepción" value={delivery.receiver_phone} t={t} />
          <Field label="Transportista" value={delivery.carrier_name || delivery.trip_carrier_name} t={t} />
          <Field label="Tracking" value={delivery.tracking_code} t={t} />
        </div>
        {delivery.notes ? (
          <div style={{ marginTop: 12 }}>
            <Field label="Notas operativas" value={delivery.notes} t={t} />
          </div>
        ) : null}
      </Card>

      <SectionTitle t={t}>Tramos y viajes</SectionTitle>
      <Card t={t} style={{ padding: 0, overflow: 'hidden' }}>
        {sortedLegs.length === 0 ? (
          <div style={{ padding: '14px 16px', fontSize: 13, color: t.muted }}>
            Sin tramos registrados. Al iniciar la entrega se crea el tramo inicial con su viaje.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
              <thead>
                <tr style={{ background: t.tableHead }}>
                  <th style={thStyle(t)}>Tramo</th>
                  <th style={thStyle(t)}>Estado</th>
                  <th style={thStyle(t)}>Viaje</th>
                  <th style={thStyle(t)}>Origen</th>
                  <th style={thStyle(t)}>Destino</th>
                  <th style={thStyle(t)}>Transportista</th>
                  <th style={thStyle(t)}>ETA</th>
                  <th style={thStyle(t)}>Tracking</th>
                  <th style={thStyle(t, 'right')}>Peso</th>
                  <th style={thStyle(t, 'right')}>Costo prorrateado</th>
                  <th style={thStyle(t, 'center')}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sortedLegs.map((leg) => (
                  <tr key={leg.id}>
                    <td style={tdStyle(t)}>
                      <div style={{ fontWeight: 700 }}>{leg.leg_code || `Tramo #${leg.id}`}</div>
                      <div style={{ fontSize: 11, color: t.muted }}>
                        Seq. {leg.sequence || '—'}
                        {leg.stop_order ? ` · Parada ${leg.stop_order}` : ''}
                      </div>
                    </td>
                    <td style={tdStyle(t)}>{leg.leg_status || '—'}</td>
                    <td style={tdStyle(t)}>
                      <div style={{ fontWeight: 600 }}>{leg.trip_code || 'Sin viaje'}</div>
                      <div style={{ fontSize: 11, color: t.muted }}>
                        {[leg.trip_type, formatDate(leg.trip_date)].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </td>
                    <td style={tdStyle(t)}>{leg.origin_location || leg.trip_origin || '—'}</td>
                    <td style={tdStyle(t)}>
                      {leg.destination_location || leg.trip_destination || '—'}
                    </td>
                    <td style={tdStyle(t)}>{leg.carrier_name || '—'}</td>
                    <td style={tdStyle(t)}>{formatDate(leg.eta_close)}</td>
                    <td style={tdStyle(t)}>{leg.tracking_code || '—'}</td>
                    <td style={tdStyle(t, 'right')}>{formatNumber(leg.weight_kg)}</td>
                    <td style={tdStyle(t, 'right')}>{formatMoney(leg.allocated_cost)}</td>
                    <td style={{ ...tdStyle(t, 'center'), whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        {typeof onEditLeg === 'function' ? (
                          <button
                            type="button"
                            title="Editar tramo"
                            aria-label="Editar tramo"
                            onClick={() => onEditLeg(leg)}
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: 8,
                              border: `1px solid ${t.cardBorder}`,
                              background: t.sectionBg,
                              color: t.text,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                            }}
                          >
                            <Pencil size={14} />
                          </button>
                        ) : null}
                        {typeof onEditTrip === 'function' ? (
                          <button
                            type="button"
                            title={leg.trip_id ? 'Editar viaje' : 'Sin viaje asignado'}
                            aria-label="Editar viaje"
                            disabled={!leg.trip_id}
                            onClick={() => onEditTrip(leg)}
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: 8,
                              border: `1px solid ${t.cardBorder}`,
                              background: t.sectionBg,
                              color: t.text,
                              cursor: leg.trip_id ? 'pointer' : 'not-allowed',
                              opacity: leg.trip_id ? 1 : 0.45,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                            }}
                          >
                            <Truck size={14} />
                          </button>
                        ) : null}
                        {typeof onDeleteLeg === 'function' ? (
                          <button
                            type="button"
                            title="Eliminar tramo"
                            aria-label="Eliminar tramo"
                            onClick={() => onDeleteLeg(leg)}
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: 8,
                              border: `1px solid ${t.cardBorder}`,
                              background: t.sectionBg,
                              color: '#dc2626',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: t.tableHead }}>
                  <td
                    colSpan={9}
                    style={{
                      ...tdStyle(t, 'right'),
                      borderBottom: 'none',
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Total costos prorrateados
                  </td>
                  <td
                    style={{
                      ...tdStyle(t, 'right'),
                      borderBottom: 'none',
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                  >
                    {formatMoney(legsCostTotal)}
                  </td>
                  <td style={{ ...tdStyle(t), borderBottom: 'none' }} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <SectionTitle t={t}>Listado de ítems</SectionTitle>
        {canEditItems && !loading ? (
          editingItems ? (
            <div style={{ display: 'inline-flex', gap: 8 }}>
              <button type="button" onClick={cancelEditingItems} disabled={itemsSaving} style={ghostBtn}>
                Cancelar
              </button>
              <button type="button" onClick={saveItems} disabled={itemsSaving} style={primaryBtn}>
                {itemsSaving ? 'Guardando…' : 'Guardar ítems'}
              </button>
            </div>
          ) : (
            <button type="button" onClick={startEditingItems} style={ghostBtn}>
              <Pencil size={13} /> Editar ítems
            </button>
          )
        ) : null}
      </div>

      <Card t={t} style={{ padding: 0, overflow: 'hidden' }}>
        {itemsError ? (
          <div
            style={{
              padding: '10px 16px',
              fontSize: 12,
              color: '#b91c1c',
              borderBottom: `1px solid ${t.cardBorder}`,
            }}
          >
            {itemsError}
          </div>
        ) : null}

        {loading ? (
          <div style={{ padding: '14px 16px', fontSize: 13, color: t.muted }}>Cargando ítems…</div>
        ) : !editingItems && itemRows.length === 0 ? (
          <div style={{ padding: '14px 16px', fontSize: 13, color: t.muted }}>
            Sin ítems asociados a esta entrega.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
              <thead>
                <tr style={{ background: t.tableHead }}>
                  <th style={thStyle(t, 'right')}>#</th>
                  <th style={thStyle(t)}>Ítem</th>
                  <th style={thStyle(t)}>SKU</th>
                  <th style={thStyle(t, 'right')}>Cantidad</th>
                  <th style={thStyle(t, 'right')}>P. unitario</th>
                  <th style={thStyle(t, 'right')}>Total</th>
                  {editingItems ? <th style={thStyle(t, 'center')}>Acciones</th> : null}
                </tr>
              </thead>
              <tbody>
                {(editingItems ? draftItems : itemRows).map((item, index) => {
                  const qty = Number(item.quantity || item.quoted_qty || 0);
                  const unit = Number(item.unit_price || 0);
                  const total = editingItems
                    ? Math.round(qty * unit * 100) / 100
                    : Number(item.line_total || 0) || Math.round(qty * unit * 100) / 100;
                  return (
                    <tr
                      key={
                        editingItems
                          ? item._key
                          : String(item.id || item.quotation_item_id || item.product_sku || index)
                      }
                    >
                      <td style={tdStyle(t, 'right')}>{index + 1}</td>
                      <td style={tdStyle(t)}>
                        <div style={{ fontWeight: 600 }}>
                          {item.product_name || item.item_name || '—'}
                        </div>
                        {item.color ? (
                          <div style={{ fontSize: 11, color: t.muted }}>{item.color}</div>
                        ) : null}
                        {!editingItems && item.source === 'quotation' ? (
                          <div style={{ fontSize: 11, color: t.muted }}>Ítem cotizado</div>
                        ) : null}
                      </td>
                      <td style={tdStyle(t)}>{item.product_sku || '—'}</td>
                      <td style={tdStyle(t, 'right')}>
                        {editingItems ? (
                          <span
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                          >
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.quantity}
                              onChange={(event) =>
                                updateDraftItem(item._key, 'quantity', event.target.value)
                              }
                              style={cellInput}
                            />
                            {item.unit ? (
                              <span style={{ color: t.muted, fontSize: 11 }}>{item.unit}</span>
                            ) : null}
                          </span>
                        ) : (
                          <>
                            {formatNumber(qty)}
                            {item.unit ? ` ${item.unit}` : ''}
                          </>
                        )}
                      </td>
                      <td style={tdStyle(t, 'right')}>
                        {editingItems ? (
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={item.unit_price}
                            onChange={(event) =>
                              updateDraftItem(item._key, 'unit_price', event.target.value)
                            }
                            style={cellInput}
                          />
                        ) : (
                          formatMoney(unit)
                        )}
                      </td>
                      <td style={tdStyle(t, 'right')}>{formatMoney(total)}</td>
                      {editingItems ? (
                        <td style={{ ...tdStyle(t, 'center'), whiteSpace: 'nowrap' }}>
                          <button
                            type="button"
                            title="Quitar ítem"
                            aria-label="Quitar ítem"
                            onClick={() => removeDraftItem(item._key)}
                            style={{ ...iconBtn, color: '#dc2626' }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
                {editingItems && draftItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ ...tdStyle(t, 'center'), color: t.muted }}>
                      Sin ítems. Agrega al menos uno o guarda para dejar la entrega sin ítems.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}

        {editingItems && addableItems.length ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 16px',
              borderTop: `1px solid ${t.cardBorder}`,
              background: t.sectionBg,
            }}
          >
            <Plus size={14} color={t.muted} />
            <span style={{ fontSize: 12, color: t.muted }}>Agregar ítem cotizado:</span>
            <select
              value=""
              onChange={(event) => {
                if (event.target.value) addDraftItem(event.target.value);
              }}
              style={{
                ...cellInput,
                textAlign: 'left',
                width: 'auto',
                minWidth: 220,
                background: t.cardBg,
                color: t.text,
              }}
            >
              <option value="">Seleccionar…</option>
              {addableItems.map((item) => (
                <option
                  key={String(item.quotation_item_id || item.product_sku)}
                  value={String(item.quotation_item_id || item.product_sku)}
                >
                  {[item.product_sku, item.product_name || item.item_name].filter(Boolean).join(' · ')}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </Card>

      {typeof onEdit === 'function' ? (
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => onEdit(delivery)}
            style={{
              height: 40,
              padding: '0 18px',
              borderRadius: 8,
              border: 'none',
              background: '#2563eb',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Editar
          </button>
        </div>
      ) : null}
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Detalle de entrega"
      subtitle="Información general, tramos/viajes e ítems enviados"
      isDarkMode={isDarkMode}
      size="xxl"
    >
      {content}
    </Dialog>
  );
};

export default DeliveryDetailModal;
